/*
  This component is meant to be rendered by one of the higher-level components like MDXSnippet, ServerOnlySnippet, or ClientOnlySnippet. It applies all of the common styles that we want in every snippet. It also takes care of certain features:
  - CopyButton to copy code to the clipboard (and the animation that follows)
  - Annotated ("highlighted") lines (via AnnotatedContainer)
  - General styling, like padding and outline.

  If we’re using this within our content, the `includeContentStyles` prop will ensure that it looks just like the snippets rendered in MDX. I can omit it when I'm doing special stuff (eg. Shadow Palette Generator).
*/

import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS, HEADER_HEIGHT, ColorMode } from '@/constants';

import {
  BaseWrapper as BaseAside,
  InfoAside,
  SuccessAside,
  WarningAside,
  QuoteAside,
} from '@/components/Aside';

import AnnotatedContainer from './AnnotatedContainer';
import WithCopyButton from './WithCopyButton';

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  code: string;
  html: string;
  clampMaxHeight?: boolean;
  lessBottomMargin?: boolean;
  lockedToColorMode?: ColorMode | false;
  includeContentStyles?: boolean;
  fontSize?: string;
  omitCopyButton?: boolean;
}

function CodeWrapper({
  code,
  html,
  clampMaxHeight,
  lessBottomMargin,
  lockedToColorMode,
  includeContentStyles = false,
  fontSize,
  omitCopyButton = false,
  style = {},
  ...delegated
}: Props) {
  if (!clampMaxHeight) {
    // Shiki automatically adds `tabindex="0"` to the <pre> tag, which is an accessibility requirement if the element is scrollable. Unless we’re clamping the max-height, though, it won't be scrollable. So we’ll remove the tabindex.
    html = html.replace(/tabindex="0"/i, '');
  }

  const OuterWrapper = includeContentStyles
    ? ContentContainer
    : Container;

  return (
    <OuterWrapper
      translate="no"
      data-locked-to-color-mode={lockedToColorMode}
      data-less-bottom-margin={String(!!lessBottomMargin)}
      style={{
        ...style,
        '--max-height': clampMaxHeight ? '75vh' : 'auto',
        '--code-font-size': fontSize,
      }}
      {...delegated}
    >
      {omitCopyButton ? (
        <Wrapper dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <WithCopyButton
          code={code}
          lockedToColorMode={lockedToColorMode}
        >
          <WrapperAccountingForCopyButton
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </WithCopyButton>
      )}
    </OuterWrapper>
  );
}

/*
  So, there's kind of a funky setup here, with "Container" and "Wrapper":

  • Container creates an `inline-size` container that Wrapper can use in container queries. We need 2 elements for this reason alone.
  • On mobile, `Wrapper` becomes a scroll container, so that the code snippet can overflow rather than awkwardly line-wrapping. But we don't want to put *everything* in this scroll container; For example, the "CopyButton" should sit within the Container, but not be within the scroll container.
*/
export const Container = styled(AnnotatedContainer)`
  --copy-button-size: 44px;
  --copy-button-margin: 8px;

  /* Contain the shimmer effect on copy: */
  position: relative;

  container-type: inline-size;
  isolation: isolate;

  /*
    Shiki works by setting '--color-light' and '--color-dark' color values on each token, but it doesn’t actually apply either of them by default (the way I configured it).
  */
  html[data-color-mode='light'] & .shiki,
  html[data-color-mode='light'] & .shiki span,
  &&[data-locked-to-color-mode='light'] .shiki,
  &&[data-locked-to-color-mode='light'] .shiki span {
    color: var(--shiki-light);
    background-color: var(--shiki-light-bg);
    font-style: var(--shiki-light-font-style);
    font-weight: var(--shiki-light-font-weight);
    text-decoration: var(--shiki-light-text-decoration);
  }
  html[data-color-mode='dark'] & .shiki,
  html[data-color-mode='dark'] & .shiki span,
  &&[data-locked-to-color-mode='dark'] .shiki,
  &&[data-locked-to-color-mode='dark'] .shiki span {
    color: var(--shiki-dark);
    background-color: var(--shiki-dark-bg);
    font-style: var(--shiki-dark-font-style);
    font-weight: var(--shiki-dark-font-weight);
    text-decoration: var(--shiki-dark-text-decoration);
  }
`;

const ContentContainer = styled(Container)`
  --copy-button-sticky-top: ${HEADER_HEIGHT}rem;
  --scrollbar-thumb: var(--color-gray-400);
  --scrollbar-bg: var(--syntax-bg);
  max-height: var(--max-height);
  margin-inline: calc(var(--viewport-padding) * -1);
  margin-bottom: 64px;
  background: var(--syntax-bg);
  border-radius: 6px;
  transition: background var(--color-swap-duration);

  &[data-less-bottom-margin='true'] {
    margin-bottom: 32px;
  }

  /*
    In dark mode, the background is transparent, and so we delineate the snippet with an outline.
  */
  html[data-color-mode='dark'] &,
  &[data-locked-to-color-mode='dark'] {
    outline: 1px solid var(--color-content-outline);
    outline-offset: -1px;
  }
  /* Inside an <Aside>, however, we don't need the outline */
  html ${BaseAside} & {
    outline: none;
    margin-bottom: 40px;

    &[data-less-bottom-margin='true'] {
      margin-bottom: 24px;
    }

    /*
      Asides eat up 8px of horizontal padding, and when the code snippet is the final child within the aside, it should eat some of the bottom padding to match.
    */
    &:last-child {
      margin-bottom: -8px;
    }
  }

  @media ${BREAKPOINTS.mdAndSmaller} {
    /*
      On smaller screens, the snippet sits right against the viewport. This is fine in Dark Mode, since mobile screens have a black bezel around them, but it looks funny with rounded corners in Light Mode.
    */
    html[data-color-mode='light'] & {
      border-radius: 0;
    }
  }

  ${InfoAside} & {
    --scrollbar-bg: var(--color-info-200);
    background: var(--color-info-200);
  }
  ${WarningAside} & {
    --scrollbar-bg: var(--color-warning-200);
    background: var(--color-warning-200);
  }
  ${SuccessAside} & {
    --scrollbar-bg: var(--color-success-200);
    background: var(--color-success-200);
  }

  /* Mobile/Tablet styles for CodeSnippet inside <Aside> */
  @media ${BREAKPOINTS.mdAndSmaller} {
    ${BaseAside} & {
      border-radius: 0px;

      /*
      On mobile, we want to expand right up to the edge, taking into account that asides have a 3px border along the left edge.
    */
      margin-left: calc(var(--viewport-padding) * -1);
      margin-right: calc(var(--viewport-padding) * -1);
      --inline-padding-override: calc(
        var(--viewport-padding) - var(--aside-border-width)
      );
    }

    /* On mobile/tablet, the code snippet becomes full-width, and so we should eat up *all* of the bottom padding: */
    html ${BaseAside} &:last-child {
      margin-bottom: -24px;
    }

    ${InfoAside} & {
      border-left: var(--aside-border-width) solid
        var(--color-info-700);
    }
    ${WarningAside} & {
      border-left: var(--aside-border-width) solid
        var(--color-warning-700);
    }
    ${SuccessAside} & {
      border-left: var(--aside-border-width) solid
        var(--color-success-700);
    }
    ${QuoteAside} & {
      /* We don't *really* support code snippets inside Quote Asides, but I'm adding this so that it doesn't look obviously broken if I do: */
      border-left: var(--aside-border-width) solid
        var(--color-info-400);
    }
  }

  /* Desktop styles for CodeSnippet inside <Aside> */
  @media ${BREAKPOINTS.mdAndLarger} {
    ${BaseAside} & {
      /*
        The <Aside> itself is pushed in by “--viewport-padding”; we want to consume half of that space.
      */
      padding-inline: 0;
      --inline-padding-override: calc(var(--viewport-padding) * 0.5);
      margin-inline: calc(var(--viewport-padding) * -0.5);
      border-radius: 6px;
    }
  }
`;

export const Wrapper = styled.div`
  /*
    Padding is pretty complicated here.
    In most situations, whether it's within MDX or elsewhere (eg. Shadow Palette Generator), we want a consistent amount of padding. The top/bottom padding is specified here, the left/right padding is specified in AnnotationContainer (since it's applied to each line, for proper highlighting).
    In SOME situations, though, when the snippet is part of a custom widget, we need to override the padding. We can do this by specifying two CSS variables:

    --block-padding-override: 0px;
    --inline-padding-override: 0px;
  */
  --default-block-padding: var(--viewport-padding);
  --block-padding: var(
    --block-padding-override,
    var(--default-block-padding)
  );
  padding-block: var(--block-padding);

  /* In certain contexts, we may wish to stretch the code snippet to fill the parent container, or take on an explicit height. */
  height: var(--code-snippet-height);

  /* If this snippet has set “clampMaxHeight”, the parent will have a known height. We want to limit this element to this value, and make it a scroll container. It'll have no effect if there is no “height” set on the parent. */
  max-height: 100%;
  overflow: auto;
  line-height: 1.6;
  font-size: var(--code-font-size, 1rem);
  letter-spacing: 0em;

  ${BaseAside} && {
    font-size: 0.9375rem;
  }

  /*
    On desktop, we want to line-wrap particularly long lines. On mobile, *every* line will be too long, and it's just a mess if we clamp it. We already have “overflow: auto” for the *vertical* scrollbar, and so we’ll disable line-wrapping on mobile to apply a horizontal scrollbar as well.
  */
  @container (min-width: 42.875rem) {
    pre {
      white-space: pre-wrap !important;
    }
  }

  @media ${BREAKPOINTS.smAndLarger} {
    /* Optical alignment */
    --default-block-padding: calc(var(--viewport-padding) - 8px);
  }

  @media ${BREAKPOINTS.mdAndLarger} {
    ${BaseAside} & {
      --default-block-padding: calc(
        var(--viewport-padding) * 0.5 - 4px
      );
    }
  }

  code {
    font-family: var(--font-family-mono);
  }
`;

const WrapperAccountingForCopyButton = styled(Wrapper)`
  /*
    Alright, so this is where things get a bit complicated.
    Right above this Wrapper, there's a CopyButton, and it uses 'position: sticky'. Sticky elements are in-flow, which means this row will take up --copy-button-size (eg. 44px) plus --copy-button-margin (eg. 8px) on the top and bottom.

    We'll apply a negative margin to eat up all this space.

    NOTE: We *only* deal with top/bottom (block) margin/padding here. The inline padding is dealt with in AnnotatedContainer.
  */
  margin-top: calc(
    var(--copy-button-size) * -1 + var(--copy-button-margin) * -2
  );
`;

export default CodeWrapper;
